/**
 * Data Quality Category Types - Client Side
 * ============================================================================
 * Client-side type definitions for the Data Quality Category Explanation Drawer.
 * These mirror the server-side types for API responses.
 *
 * Related Files:
 * - server/dataQuality/categoryTypes.ts (server types)
 * - client/components/dataQuality/DataQualityCategoryDrawer.tsx (consumer)
 * - client/components/dataQuality/useDQCategoryDrawer.ts (API hook)
 *
 * @version 1.0.0
 * @created 2026-01-28
 * ============================================================================
 */

// ============================================================================
// Category IDs
// ============================================================================

/**
 * Union type of all Data Quality category identifiers
 */
export type DataQualityCategoryId =
  | 'information_content'
  | 'statistical_quality'
  | 'redundancy'
  | 'outliers'
  | 'sparsity'
  | 'data_cleanliness'
  | 'anomaly_detection'
  | 'textual_quality';

/**
 * Human-readable display names for each category
 */
export const CATEGORY_DISPLAY_NAMES: Record<DataQualityCategoryId, string> = {
  information_content: 'Information Content',
  statistical_quality: 'Statistical Quality',
  redundancy: 'Independence',
  outliers: 'Outliers',
  sparsity: 'Completeness',
  data_cleanliness: 'Data Cleanliness',
  anomaly_detection: 'Anomaly Detection',
  textual_quality: 'Textual Quality',
};

/**
 * Category icons
 */
export const CATEGORY_ICONS: Record<DataQualityCategoryId, string> = {
  information_content: '📊',
  statistical_quality: '📈',
  redundancy: '🔗',
  outliers: '🎯',
  sparsity: '✅',
  data_cleanliness: '🧹',
  anomaly_detection: '🔍',
  textual_quality: '📝',
};

// ============================================================================
// Data Governance Citations
// ============================================================================

/**
 * A citation from a data governance framework or standard.
 *
 * v2.5 (DC-1666): `source` defaults to 'framework' when absent. When 'internal'
 * the citation is an approved clause from the customer's own governance document
 * and carries provenance for the [INTERNAL] drawer card.
 */
export interface DataGovernanceCitation {
  framework: string;
  document: string;
  section: string;
  text: string;
  url?: string;

  // v2.5 DC-1666: source discriminator + internal provenance
  source?: 'framework' | 'internal';
  documentId?: string;
  documentTitle?: string;
  uploadedBy?: string;
  uploadedAt?: string;
  mappingId?: string;
  confidence?: number;
}

// ============================================================================
// Score Thresholds
// ============================================================================

/**
 * Threshold configuration for score interpretation
 */
export interface ScoreThresholds {
  excellent: number;
  good: number;
  poor: number;
}

// ============================================================================
// Findings
// ============================================================================

/**
 * A specific finding from the analysis
 */
export interface CategoryFinding {
  type: 'critical' | 'warning' | 'info' | 'positive';
  message: string;
  columns?: string[];
  value?: number | string;
}

// ============================================================================
// Sub-Metrics
// ============================================================================

/**
 * Value of a sub-metric extracted from analysis results
 */
export interface SubMetricValue {
  id: string;
  name: string;
  value: number | string | null;
  unit: string;
  status: 'excellent' | 'good' | 'warning' | 'critical' | 'not_available';
  details?: string;
}

// ============================================================================
// Recommendations
// ============================================================================

/**
 * A specific recommendation for improvement
 */
export interface CategoryRecommendation {
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  impactEstimate?: number;
}

// ============================================================================
// Main Response Type
// ============================================================================

/**
 * Full detail response for a Data Quality category
 */
export interface DataQualityCategoryDetail {
  categoryId: DataQualityCategoryId;
  name: string;
  icon: string;

  // Assessment Result
  score: number;
  rating: 'excellent' | 'good' | 'poor' | 'critical';
  assessmentSummary: string;

  // Key Findings
  findings: CategoryFinding[];

  // Educational Content
  whatItMeasures: string;
  whyItMatters: string;
  thresholds: ScoreThresholds;

  // Sub-Metrics Breakdown
  subMetrics: SubMetricValue[];

  // Citations
  citations: DataGovernanceCitation[];

  // Recommendations
  recommendations: CategoryRecommendation[];

  // Metadata
  wasAnalyzed: boolean;
  notAnalyzedReason?: string;
}

// ============================================================================
// API Response Type
// ============================================================================

/**
 * API response type for the category detail endpoint
 */
export interface GetCategoryDetailResponse {
  success: boolean;
  data?: DataQualityCategoryDetail;
  error?: string;
}

// ============================================================================
// Drawer Event Types
// ============================================================================

/**
 * Custom event detail for DQ category click events
 */
export interface DQCategoryClickEventDetail {
  categoryId: DataQualityCategoryId;
  score: number;
}

/**
 * Declare custom event type on Window
 */
declare global {
  interface WindowEventMap {
    'dq-category-click': CustomEvent<DQCategoryClickEventDetail>;
  }

  interface Window {
    /** Mount the DQ Category Drawer for standalone (vanilla JS) mode */
    mountDQCategoryDrawer?: (jobId: string) => void;
    /** Unmount the DQ Category Drawer */
    unmountDQCategoryDrawer?: () => void;
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if a string is a valid category ID
 */
export function isValidCategoryId(id: string): id is DataQualityCategoryId {
  const validIds: DataQualityCategoryId[] = [
    'information_content',
    'statistical_quality',
    'redundancy',
    'outliers',
    'sparsity',
    'data_cleanliness',
    'anomaly_detection',
    'textual_quality',
  ];
  return validIds.includes(id as DataQualityCategoryId);
}

/**
 * Get CSS class for rating
 */
export function getRatingClass(rating: 'excellent' | 'good' | 'poor' | 'critical'): string {
  switch (rating) {
    case 'excellent':
      return 'dq-category__status--excellent';
    case 'good':
      return 'dq-category__status--good';
    case 'poor':
      return 'dq-category__status--poor';
    case 'critical':
      return 'dq-category__status--critical';
    default:
      return '';
  }
}

/**
 * Get display label for rating
 */
export function getRatingLabel(rating: 'excellent' | 'good' | 'poor' | 'critical'): string {
  switch (rating) {
    case 'excellent':
      return 'Excellent';
    case 'good':
      return 'Good';
    case 'poor':
      return 'Needs Improvement';
    case 'critical':
      return 'Critical';
    default:
      return rating;
  }
}

/**
 * Get CSS class for finding type
 */
export function getFindingClass(type: CategoryFinding['type']): string {
  switch (type) {
    case 'critical':
      return 'dq-category__finding--critical';
    case 'warning':
      return 'dq-category__finding--warning';
    case 'info':
      return 'dq-category__finding--info';
    case 'positive':
      return 'dq-category__finding--positive';
    default:
      return '';
  }
}

/**
 * Get icon for finding type
 */
export function getFindingIcon(type: CategoryFinding['type']): string {
  switch (type) {
    case 'critical':
      return '❌';
    case 'warning':
      return '⚠️';
    case 'info':
      return 'ℹ️';
    case 'positive':
      return '✅';
    default:
      return '';
  }
}

/**
 * Get CSS class for sub-metric status
 */
export function getSubMetricStatusClass(status: SubMetricValue['status']): string {
  switch (status) {
    case 'excellent':
      return 'dq-category__submetric--excellent';
    case 'good':
      return 'dq-category__submetric--good';
    case 'warning':
      return 'dq-category__submetric--warning';
    case 'critical':
      return 'dq-category__submetric--critical';
    case 'not_available':
      return 'dq-category__submetric--na';
    default:
      return '';
  }
}

/**
 * Get CSS class for priority
 */
export function getPriorityClass(priority: CategoryRecommendation['priority']): string {
  switch (priority) {
    case 'high':
      return 'dq-category__priority--high';
    case 'medium':
      return 'dq-category__priority--medium';
    case 'low':
      return 'dq-category__priority--low';
    default:
      return '';
  }
}
